import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createGradeLevel,
  updateGradeLevel,
} from "../redux/actions/gradeLevelActions";
import { gradeLevelActions } from "../redux/slices/gradeLevel";
import { cleanFormData } from "../helpers/formHelpers";

import { GradeLevelResponseDto } from "../shared/dtos/gradeLevel/GradeLevelResponseDto";
import { GradeLevelRequestDto } from "../shared/dtos/gradeLevel/GradeLevelRequestDto";

const gradeLevelSchema = Yup.object().shape({
  name: Yup.string().required("Grade name is required (e.g., Grade 5)"),
  levelOrder: Yup.number()
    .required("Level order is required")
    .integer("Must be a whole number")
    .positive("Must be a positive number"),
  description: Yup.string().required("Description is required"),
});

interface CreateOrUpdateGradeLevelFormProps {
  gradeLevel?: GradeLevelResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateGradeLevelForm: React.FC<
  CreateOrUpdateGradeLevelFormProps
> = ({
  gradeLevel = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: GradeLevelRequestDto = {
    name: gradeLevel?.name || "",
    levelOrder: gradeLevel?.levelOrder || 1,
    description: gradeLevel?.description || "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(gradeLevelActions.setErrorUpdate(null))
            : () => dispatch(gradeLevelActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={gradeLevelSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: GradeLevelRequestDto = {
            name: rawCleaned.name,
            levelOrder: Number(rawCleaned.levelOrder),
            description: rawCleaned.description,
          };

          if (isEditing && gradeLevel) {
            dispatch(
              updateGradeLevel({ id: gradeLevel.id, values: cleanedValues }),
            );
          } else {
            dispatch(createGradeLevel(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<GradeLevelRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="name"
                  label="Grade Name"
                  placeholder="e.g., Grade 5"
                />

                <FormTextField
                  name="levelOrder"
                  label="Level Order (Sequence)"
                  type="number"
                  placeholder="e.g., 5"
                  helperText="Used for sorting grades (1, 2, 3...)"
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </Box>

              <FormTextField
                name="description"
                label="Description"
                multiline
                rows={3}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Grade"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateGradeLevelForm;
